import { useDragOperation, useDroppable } from '@dnd-kit/react';
import type { DragEndEvent } from '@dnd-kit/react';
import { useSortable } from '@dnd-kit/react/sortable';
import { useMemo } from 'react';
import type { ReactNode } from 'react';

import type { Principal, WorkflowDefinition, WorkflowStage } from '../shared/index.js';
import { allowedTargetStageIds, visibleStages } from '../shared/index.js';

import type { BoardCard, CardPlacement, DirectoryUser } from '../api/endpoints.js';
import { TONE_DOT, TicketCard } from './ticket-card.js';

interface KanbanProps {
  readonly workflow: WorkflowDefinition;
  readonly tickets: readonly BoardCard[];
  readonly principal: Principal;
  readonly people: ReadonlyMap<string, DirectoryUser>;
  readonly onOpen: (ticketId: string) => void;
}

interface CardProps {
  readonly ticket: BoardCard;
  readonly index: number;
  readonly people: ReadonlyMap<string, DirectoryUser>;
  readonly onOpen: (ticketId: string) => void;
}

function SortableCard({ ticket, index, people, onOpen }: CardProps): ReactNode {
  const { ref, isDragging } = useSortable({
    id: ticket.id,
    index,
    type: 'card',
    accept: ['card'],
    group: ticket.stageId,
    disabled: { droppable: true },
  });

  return (
    <div ref={ref} data-ticket-id={ticket.id}>
      <TicketCard ticket={ticket} people={people} onOpen={onOpen} dragging={isDragging} />
    </div>
  );
}

interface ColumnProps {
  readonly stage: WorkflowStage;
  readonly tickets: readonly BoardCard[];
  readonly locked: boolean;
  readonly people: ReadonlyMap<string, DirectoryUser>;
  readonly onOpen: (ticketId: string) => void;
}

function Column({ stage, tickets, locked, people, onOpen }: ColumnProps): ReactNode {
  const { ref, isDropTarget } = useDroppable({
    id: `column:${stage.id}`,
    type: 'column',
    accept: ['card'],
    disabled: locked,
  });

  return (
    <section
      ref={ref}
      className={`flex w-[82vw] max-w-[300px] shrink-0 snap-start flex-col rounded-card border transition sm:w-[288px] ${
        locked
          ? 'border-dashed border-hairline bg-mist/40 opacity-45 dark:border-dark-line dark:bg-dark-board/40'
          : isDropTarget
            ? 'border-royal/40 bg-royal/6'
            : 'border-hairline bg-board dark:border-dark-line dark:bg-dark-board'
      }`}
    >
      <header className="flex items-center gap-2 px-4 pt-4 pb-3">
        <span className={`size-2 rounded-full ${TONE_DOT[stage.tone]}`} />
        <h3 className="text-[13px] font-bold text-navy dark:text-dark-ink">{stage.label}</h3>
        <span className="ml-auto rounded-full bg-navy/6 px-2 py-0.5 text-[11px] font-bold text-muted dark:bg-white/10">
          {tickets.length}
        </span>
      </header>

      <div className="flex min-h-[120px] flex-1 flex-col gap-2.5 px-3 pb-3">
        {tickets.map((ticket, index) => (
          <SortableCard
            key={ticket.id}
            ticket={ticket}
            index={index}
            people={people}
            onOpen={onOpen}
          />
        ))}

        {tickets.length === 0 ? (
          <p className="px-1 py-6 text-center text-[12.5px] text-muted">
            {locked ? 'No move to here' : 'Nothing here'}
          </p>
        ) : null}
      </div>
    </section>
  );
}

function groupByStage(
  stages: readonly WorkflowStage[],
  tickets: readonly BoardCard[],
): Map<string, BoardCard[]> {
  const grouped = new Map<string, BoardCard[]>();

  for (const stage of stages) grouped.set(stage.id, []);

  for (const ticket of tickets) {
    const list = grouped.get(ticket.stageId);
    if (list !== undefined) list.push(ticket);
  }

  for (const list of grouped.values()) {
    list.sort(
      (left, right) =>
        Number.parseFloat(left.boardPosition) - Number.parseFloat(right.boardPosition),
    );
  }

  return grouped;
}

function insertionIndex(
  columnElement: Element | null | undefined,
  column: readonly BoardCard[],
  pointerY: number,
): number {
  if (!(columnElement instanceof HTMLElement)) return column.length;

  for (const [index, ticket] of column.entries()) {
    const element = columnElement.querySelector(`[data-ticket-id="${ticket.id}"]`);

    if (!(element instanceof HTMLElement)) continue;

    const box = element.getBoundingClientRect();

    if (pointerY < box.top + box.height / 2) return index;
  }

  return column.length;
}

export function resolveCardDrop(
  event: DragEndEvent,
  tickets: readonly BoardCard[],
  workflow: WorkflowDefinition,
): { readonly ticketId: string; readonly placement: CardPlacement } | null {
  const source = event.operation.source;
  const target = event.operation.target;

  if (event.canceled || source === null) return null;

  if (target === null || target === undefined) return null;

  const sourceId = String(source.id);
  const card = tickets.find((ticket) => ticket.id === sourceId);

  if (card === undefined) return null;

  const targetId = String(target.id);

  if (!targetId.startsWith('column:')) return null;

  const stageId = targetId.slice('column:'.length);
  const byStage = groupByStage(visibleStages(workflow), tickets);
  const column = (byStage.get(stageId) ?? []).filter((ticket) => ticket.id !== sourceId);
  const index = insertionIndex(target.element, column, event.operation.position.current.y);

  const currentIndex = (byStage.get(card.stageId) ?? []).findIndex(
    (ticket) => ticket.id === sourceId,
  );

  if (stageId === card.stageId && index === currentIndex) return null;

  const before = column[index];
  const after = column[index - 1];

  return {
    ticketId: sourceId,
    placement:
      before !== undefined
        ? { stageId, beforeTicketId: before.id }
        : after !== undefined
          ? { stageId, afterTicketId: after.id }
          : { stageId },
  };
}

export function Kanban({ workflow, tickets, principal, people, onOpen }: KanbanProps): ReactNode {
  const stages = useMemo(() => visibleStages(workflow), [workflow]);
  const byStage = useMemo(() => groupByStage(stages, tickets), [stages, tickets]);

  return (
    <BoardColumns
      stages={stages}
      byStage={byStage}
      tickets={tickets}
      workflow={workflow}
      principal={principal}
      people={people}
      onOpen={onOpen}
    />
  );
}

interface BoardColumnsProps {
  readonly stages: readonly WorkflowStage[];
  readonly byStage: ReadonlyMap<string, BoardCard[]>;
  readonly tickets: readonly BoardCard[];
  readonly workflow: WorkflowDefinition;
  readonly principal: Principal;
  readonly people: ReadonlyMap<string, DirectoryUser>;
  readonly onOpen: (ticketId: string) => void;
}

function BoardColumns({
  stages,
  byStage,
  tickets,
  workflow,
  principal,
  people,
  onOpen,
}: BoardColumnsProps): ReactNode {
  const { source } = useDragOperation();

  const dragged = useMemo(() => {
    if (source === null || source === undefined) return null;
    const id = String(source.id);
    return tickets.find((ticket) => ticket.id === id) ?? null;
  }, [source, tickets]);

  const openStageIds =
    dragged === null
      ? null
      : allowedTargetStageIds(workflow, principal, {
          stageId: dragged.stageId,
          requesterId: dragged.requesterId,
          assigneeId: dragged.assigneeId,
          departmentSlug: dragged.departmentSlug,
        });

  const isLocked = (stageId: string): boolean => {
    if (openStageIds === null || dragged === null) return false;
    return stageId !== dragged.stageId && !openStageIds.has(stageId);
  };

  return (
    <div className="flex flex-1 snap-x snap-mandatory gap-3 overflow-x-auto px-4 py-4 sm:snap-none sm:gap-4 sm:px-7 sm:py-5">
      {stages.map((stage) => (
        <Column
          key={stage.id}
          stage={stage}
          tickets={byStage.get(stage.id) ?? []}
          locked={isLocked(stage.id)}
          people={people}
          onOpen={onOpen}
        />
      ))}
    </div>
  );
}
